import useCachedValueIfNotNull from '@/utils/CachedValueIf';
import useDebounce from '@/utils/useDebounce';
import { useMemo, useState } from 'react';
import type { EmployeeQueryParams } from '../types';
import { useEmployees } from './useEmployees';

interface UseEmployeeListOptions {
  companyId: string;
  initialPageSize?: number;
  enabled?: boolean;
}

export function useEmployeeList({ companyId, initialPageSize = 10, enabled = true }: UseEmployeeListOptions) {
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const debouncedSearch = useDebounce(searchInput);

  const queryParams: EmployeeQueryParams = useMemo(
    () => ({
      page,
      pageSize,
      search: debouncedSearch || undefined,
      companyId
    }),
    [page, pageSize, debouncedSearch, companyId]
  );

  const { data: employeesDataResult, isLoading, isFetching } = useEmployees(queryParams, enabled);

  const serverEmployees = useCachedValueIfNotNull(
    employeesDataResult,
    isLoading,
    { data: [], total: 0, page: 1, pageSize: 10 }
  );

  const total = employeesDataResult?.total || 0;

  const employees = useMemo(() => {
    const searchLower = searchInput.toLowerCase();
    const filteredEmployees = serverEmployees.data.filter((employee) => {
      const name = employee.contact?.name || employee.user?.username || '';
      return name.toLowerCase().includes(searchLower);
    });
    
    return filteredEmployees;
  }, [serverEmployees, searchInput]);

  return {
    searchInput,
    setSearchInput,
    page,
    setPage,
    pageSize,
    setPageSize,
    selectedIds,
    setSelectedIds,
    employees,
    total,
    isLoading,
    isFetching,
  };
}
